import { useEffect, useState } from 'react';
import type { ThemeSpec } from '../themes';

const STORAGE_KEY = 'bb_onboarded_v1';

export function shouldShowOnboarding(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) !== '1';
}

export function resetOnboarding() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

interface Props {
  theme?: ThemeSpec;
  /**
   * Initial open state. The component owns its own state thereafter so the
   * "?" button can re-summon the hint after dismissal (R3-P1-6: previously
   * a one-shot dismiss that left no way back). Board.tsx still uses
   * shouldShowOnboarding() to decide whether the bubble is open on first
   * load.
   */
  initialOpen?: boolean;
}

export default function OnboardingHint({ theme, initialOpen = true }: Props) {
  const [open, setOpen] = useState(initialOpen);
  // Touch devices have no keyboard — shortcut cheatsheet is noise there.
  const isCoarse =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false;

  // R7: 按 ? 或 Shift+? 打开/关闭 cheat sheet。在 input/textarea 中不响应。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' && !(e.key === '/' && e.shiftKey)) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {}
    setOpen(false);
  }

  // Theme-aware (fixes R2-P0-3 inline-style debt). Falls back to dark on whiteboard
  // if no theme passed; whiteboard theme returns light surface so the dialog reads
  // correctly on a near-white canvas.
  const surface = theme?.surface ?? 'rgba(20,20,20,0.92)';
  const surfaceText = theme?.surfaceText ?? '#f5f5f4';
  const surfaceBorder = theme?.surfaceBorder ?? 'rgba(255,255,255,0.10)';
  const surfaceShadow = theme?.surfaceShadow ?? '0 8px 24px rgba(0,0,0,0.35)';
  const accent = theme?.accent ?? '#fbbf24';
  const accentContrast = theme?.accentContrast ?? '#1a1a1a';

  if (!open) {
    return (
      <button
        type="button"
        aria-label="打开使用提示"
        onClick={() => setOpen(true)}
        className="absolute right-4 bottom-24 z-30 w-9 h-9 rounded-full flex items-center justify-center text-[15px] font-semibold backdrop-blur-md hover:scale-105 transition-transform"
        style={{
          background: surface,
          color: surfaceText,
          border: `1px solid ${surfaceBorder}`,
          boxShadow: surfaceShadow,
        }}
      >
        ?
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="使用提示"
      className="absolute right-4 bottom-24 max-w-[320px] p-4 rounded-lg backdrop-blur-xl bb-pop z-30 text-[13px] leading-relaxed cursor-default"
      style={{
        background: surface,
        color: surfaceText,
        border: `1px solid ${surfaceBorder}`,
        boxShadow: surfaceShadow,
      }}
    >
      <div className="font-semibold mb-2 flex items-center gap-1.5">
        <span aria-hidden style={{ color: accent }}>✏️</span>
        欢迎来到南哪小黑板
      </div>
      <ul className="m-0 list-disc" style={{ paddingLeft: 18 }}>
        <li>底部工具栏选 <b>笔 / 颜色 / 粗细</b></li>
        <li>滚轮或双指 <b>平移</b>，双指捏合或 Ctrl+滚轮 <b>缩放</b>；按住空格拖动亦可平移</li>
        <li>画错了点 <b>撤销</b>（只撤销你自己最近的一笔）</li>
        <li>橡皮只能擦 <b>你自己</b> 的笔画（管理员可擦全部）</li>
        <li>悬停在笔画上可看 <b>作者名字</b>，给同学留个回应/接龙</li>
      </ul>
      <div
        className="mt-3 pt-2.5 text-[12px]"
        style={{ borderTop: `1px dashed ${surfaceBorder}` }}
      >
        <div className="font-semibold mb-1.5 opacity-80">涂鸦守则</div>
        <ol className="m-0" style={{ paddingLeft: 18, listStyle: 'decimal' }}>
          <li>不覆盖、不破坏别人的作品</li>
          <li>不写任何违法违规内容</li>
          <li>不用脚本等自动化工具刷屏（自动封禁）</li>
          <li>违规将留证并向有关部门反映</li>
        </ol>
      </div>
      {!isCoarse && (
        <div
          className="mt-3 pt-2.5 text-[12px]"
          style={{ borderTop: `1px dashed ${surfaceBorder}` }}
        >
          <div className="font-semibold mb-1.5 opacity-80">快捷键</div>
          <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
            <kbd className="font-mono opacity-90">B</kbd><span>画笔</span>
            <kbd className="font-mono opacity-90">E</kbd><span>橡皮</span>
            <kbd className="font-mono opacity-90">X</kbd><span>切换画笔/橡皮</span>
            <kbd className="font-mono opacity-90">1–9</kbd><span>粗细 1-9</span>
            <kbd className="font-mono opacity-90">Ctrl+Z</kbd><span>撤销上一笔</span>
            <kbd className="font-mono opacity-90">F</kbd><span>缩放到全部内容</span>
            <kbd className="font-mono opacity-90">Space</kbd><span>按住 + 拖动 = 平移</span>
            <kbd className="font-mono opacity-90">?</kbd><span>显示/隐藏此面板</span>
          </div>
        </div>
      )}
      <button
        onClick={dismiss}
        className="mt-3 w-full py-2 rounded-sm text-[13px] font-semibold"
        style={{ background: accent, color: accentContrast, border: 'none' }}
      >
        知道了，开始涂鸦
      </button>
    </div>
  );
}
